import { isNil } from "lodash";
import { transformVisitorStatus } from "../helpers/helpers";

const useMixpanelEvent = () => {
    const eventData = {};

    /**
     * Sends an event to Mixpanel with additional information.
     *
     * @param {string} eventName - The name of the event to be sent to Mixpanel.
     * @param {object} additionalInfo - Additional information for constructing the event data.
     * @param {boolean} additionalInfo.isAuthenticated - The authentication status of the user.
     * @param {string} additionalInfo.visitorStatus - The visitor status (new-guest, repeat-guest, registered).
     * @param {string} additionalInfo.pageName - The name of the page where the event occurred (e.g., "Property Feed - Trending", "Property Details").
     * @param {string} additionalInfo.propertyUrl - The URL of the property being shared.
     * @param {string} additionalInfo.referringUrl - The URL of the page that referred the user.
     * @param {string} additionalInfo.source - The source of the registration (e.g., "social", "challenge", "daily").
     * @param {boolean} additionalInfo.hasCommentHashtag - Whether the comment includes a hashtag.
     * @param {string} additionalInfo.commentType - The type of comment (1st level, 2nd level, 3rd level).
     * @param {string} additionalInfo.hashtagName - The name of the hashtag being created.
     * @param {string} additionalInfo.hashtag - The hashtag being voted on.
     * @param {string|number|null} additionalInfo.user_id - The user identifier.
     * @param {string|null} additionalInfo.nickname - The user's nickname.
     * @param {string|null} additionalInfo.email - The user's email address.
     * @param {string} additionalInfo.action - The action performed (e.g., 'add', 'remove').
     * @param {string} additionalInfo.reaction - The reaction (e.g., '😍 - U+1F60D').
     * @param {string} additionalInfo.pageUrl - The URL of the page the user is on.
     * @param {string} additionalInfo.agentName - The agent's full name (first name + last name).
     * @param {string} additionalInfo.agentMlsId - The agent's MLS ID.
     * @param {string} additionalInfo.clickType - The type of click (external link, instagram, tiktok, x, facebook, youtube).
     * @param {string} additionalInfo.clickLocation - The location where the click occurred (property_page, daily_score_modal).
     *
     * @returns {void}
     */
    const sendEvent = (eventName, additionalInfo) => {
        try {
            const mixpanel = window?.mixpanel;
            if (!mixpanel) {
                console.warn("Mixpanel not available for tracking event:", eventName);
                return;
            }

            const track = mixpanel.track;
            if (typeof track !== "function") {
                console.warn("Mixpanel track method not available");
                return;
            }

            const {
                isAuthenticated,
                visitorStatus,
                pageName,
                propertyUrl,
                referringUrl,
                source,
                hasCommentHashtag,
                commentType,
                hashtagName,
                hashtag,
                user_id,
                nickname,
                email,
                action,
                reaction,
                pageUrl,
                guess_number,
                userScore,
                location,
                agentName,
                agentMlsId,
                clickType,
                clickLocation,
            } = additionalInfo;

            if (!isNil(isAuthenticated)) {
                let userStatus = "guest";

                if (isAuthenticated) {
                    userStatus = "registered";
                }

                eventData["user_status"] = userStatus;
            }

            if (!isNil(visitorStatus) && !isAuthenticated) {
                eventData["visitor_status"] = transformVisitorStatus(visitorStatus);
            }

            if (!isNil(pageName)) {
                eventData["page_name"] = pageName;
            }

            if (!isNil(propertyUrl)) {
                eventData["property_url"] = propertyUrl;
            }

            if (!isNil(referringUrl)) {
                eventData["referring_url"] = referringUrl;
            }

            if (!isNil(source)) {
                eventData["source"] = source;
            }

            if (!isNil(action)) {
                eventData["action"] = action;
            }

            if (!isNil(reaction)) {
                eventData["reaction"] = reaction;
            }

            if (!isNil(hasCommentHashtag)) {
                eventData["comment_hashtag"] = hasCommentHashtag ? "Y" : "N";
            }

            if (!isNil(commentType)) {
                eventData["comment_type"] = commentType;
            }

            if (!isNil(hashtagName)) {
                eventData["hashtag_name"] = hashtagName;
            }

            if (!isNil(hashtag)) {
                eventData["hashtag"] = hashtag;
            }

            if (!isNil(user_id)) {
                eventData["user_id"] = user_id;
            }

            if (!isNil(nickname)) {
                eventData["nickname"] = nickname;
            }

            if (!isNil(email)) {
                eventData["email"] = email;
            }

            if (!isNil(pageUrl)) {
                eventData["page_url"] = pageUrl;
            }

            if (!isNil(guess_number)) {
                eventData["guess_number"] = guess_number;
            }

            if (!isNil(userScore)) {
                eventData["user_score"] = userScore;
            }

            if (!isNil(location)) {
                eventData["location"] = location;
            }

            if (!isNil(agentName)) {
                eventData["agent_name"] = agentName;
            }

            if (!isNil(agentMlsId)) {
                eventData["agent_mls_id"] = agentMlsId;
            }

            if (!isNil(clickType)) {
                eventData["click_type"] = clickType;
            }

            if (!isNil(clickLocation)) {
                eventData["click_location"] = clickLocation;
            }

            // Send event to Mixpanel
            track.call(mixpanel, eventName, eventData);
        } catch (error) {
            console.error("Error tracking Mixpanel event:", error);
        }
    };

    return { sendEvent };
};

export default useMixpanelEvent;
